import React, { useRef, useEffect, useState, useCallback }  from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { motion } from "framer-motion";

import "./index.scss";

import SkeletonLoader from "../../../../../ui/SkeletonLoader";

import { useScanInfoContext } from "../../../../../../pages/ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getVulnerabilitiesTrend } from "../../../../../../store/actions/thirdPartyRiskAction";


const VulnerabilityTrend = () => {

    const vulnerabilitiesTrendResponse = useSelector((state) => state.thirdPartyRisk.vulnerabilitiesTrendStats);
    
    const {
        domainId,
        scanId,
        organizationId,
        triggerStatsFetch,
        setTriggerStatsFetch,
    } = useScanInfoContext();

    const dispatch = useDispatch();

    const chartRef = useRef(null);

    const [vulnerabilitiesTrendLoading, setVulnerabilitiesTrendLoading] = useState(false);
    const [vulnerabilitiesTrend, setVulnerabilitiesTrend] = useState(null);

    const fetchVulnerabilitiesTrend = useCallback(()=>{
        setVulnerabilitiesTrendLoading(true);
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`;
        dispatch(getVulnerabilitiesTrend(params))
    },[organizationId, domainId, scanId, dispatch])

    useEffect(()=>{
        if (triggerStatsFetch) {
            fetchVulnerabilitiesTrend();
            setTriggerStatsFetch(false);
        }
    },[triggerStatsFetch, setTriggerStatsFetch, fetchVulnerabilitiesTrend])

    useEffect(()=>{
        if(vulnerabilitiesTrendResponse !== null) {
            setVulnerabilitiesTrendLoading(false)
            setVulnerabilitiesTrend(vulnerabilitiesTrendResponse)
        }
    },[vulnerabilitiesTrendResponse, dispatch])

    useEffect(()=>{
        if(vulnerabilitiesTrend !== null) {  

            // ---- Data (use \n to force 2-line labels) ----
            const transformVulnStatsToChartData = (apiResponse) => {
                const trends = apiResponse?.stats?.domain_vulnerability_trends;
                if (!trends) return [];

                // Optional: map specific keys to prettier labels
                const keyToCategory = {
                    cves: "CVEs",
                    ssl_vulnerabilities: "SSL\nVulnerabilities",
                    subdomain_takeovers: "Subdomain\nTakeovers",
                };

                const formatKey = (key) => {
                    // Fallback formatter if key is not in keyToCategory
                    const words = key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1));
                    if (words.length > 1) {
                        // Put last word on a new line: "Subdomain\nTakeovers"
                        return `${words.slice(0, -1).join(" ")}\n${words[words.length - 1]}`;
                    }
                    return words[0];
                };

                return Object.entries(trends).map(([key, sev]) => {
                    const low = sev?.low ?? 0;
                    const medium = sev?.medium ?? 0;
                    const high = sev?.high ?? 0;
                    const critical = sev?.critical ?? 0;

                    return {
                        category: keyToCategory[key] || formatKey(key),
                        low,
                        medium,
                        high,
                        critical,
                        total: low + medium + high + critical,
                    };
                });
            };
            let data = null
            if(vulnerabilitiesTrend?.status === "failed") {
                data = [
                    { "category":"CVEs","critical": 0,"high": 0,"medium": 0,"low": 0,"total":0 },
                    { "category":"SSL\nVulnerabilities","critical": 0,"high": 0,"medium": 0,"low": 0,"total":0 },
                    { "category":"Subdomain\nTakeovers","critical": 0,"high": 0,"medium": 0,"low": 0,"total":0 }
                ]
            } else {                
                data = transformVulnStatsToChartData(vulnerabilitiesTrend);
            }            

            const root = am5.Root.new(chartRef.current);
            root.setThemes([am5themes_Animated.new(root)]);

            // Remove watermark
            if (root._logo) root._logo.dispose();

            // Normalize & ensure totals
            const normalized = (data || []).map(d => {
                const low = Number(d.low || 0);
                const med = Number(d.medium || 0);
                const high = Number(d.high || 0);
                const crit = Number(d.critical || 0);
                const total = d.total != null ? Number(d.total) : low + med + high + crit;
                return { category: d.category, low, medium: med, high, critical: crit, total };
            });

            // --------- Y axis math ---------
            const rawMax = normalized.length ? Math.max(...normalized.map(d => d.total)) : 0;
            const roundedMax = Math.ceil(rawMax / 10) * 10; // round up to nearest 10
            const steps = 5;                                 // 5 equal intervals
            const stepSize = roundedMax / steps;
            const targetMax = roundedMax + stepSize;         // add one step headroom

            // --------- Chart ---------
            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    layout: root.verticalLayout,
                    paddingLeft: 0,
                    paddingRight: 0
                })
            );

            // --------- X Axis ---------
            const xRenderer = am5xy.AxisRendererX.new(root, {
                minGridDistance: 20,
                cellStartLocation: 0.15,
                cellEndLocation: 0.85
            });

            // remove vertical gridlines
            xRenderer.grid.template.setAll({ forceHidden: true });

            // wrap/center labels (and \n forces two lines)
            xRenderer.labels.template.setAll({
                textAlign: "center",
                oversizedBehavior: "wrap",
                maxWidth: 120,
                lineHeight: 1.2
            });

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "category",
                    renderer: xRenderer
                })
            );
            xAxis.data.setAll(normalized);

            // --------- Y Axis ---------
            const yRenderer = am5xy.AxisRendererY.new(root, { minGridDistance: 15 });
            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    min: 0,
                    max: targetMax,
                    strictMinMax: true,
                    renderer: yRenderer
                })
            );

            // Hide default grid & labels; draw our own
            yRenderer.grid.template.setAll({ forceHidden: true });
            yRenderer.labels.template.setAll({ forceHidden: true });

            const roundTo10 = (val) => Math.ceil(val / 10) * 10;

            // Dotted horizontal gridlines & labels at 6 ticks (0..targetMax)
            const addYMark = (value) => {
                const di = yAxis.makeDataItem({ value });
                const range = yAxis.createAxisRange(di);

                range.get("grid").setAll({
                    forceHidden: false,
                    strokeOpacity: 0.5,
                    strokeDasharray: [4, 4],
                    stroke: am5.color(0x999999),
                    location: 1
                });

                range.get("label").setAll({
                    forceHidden: false,
                    text: String(roundTo10(value)),
                    centerY: am5.p50,
                    location: 1,
                    paddingRight: 8
                });
            };
            for (let i = 0; i <= steps; i++) {
                addYMark((i * targetMax) / steps);
            }

            // --------- Helper to create stacked series ---------
            const makeSeries = (name, field, colorHex, opacity = 0.7) => {
                const series = chart.series.push(
                    am5xy.ColumnSeries.new(root, {
                        name,
                        xAxis,
                        yAxis,
                        stacked: true,
                        clustered: false,              // ✅ align stacks with background
                        valueYField: field,
                        categoryXField: "category",
                        calculateAggregates: true
                    })
                );

                series.columns.template.setAll({
                    tooltipText: `${name}: {valueY}`,
                    strokeOpacity: 0,
                    fill: am5.color(`#${colorHex}`),
                    fillOpacity: opacity
                });

                series.data.setAll(normalized);
                // ensure stacks render above background
                series.set("zIndex", 10);
                return series;
            };

            // Stacked series (colors per spec)
            const lowSeries      = makeSeries("Low",      "low",      "3FA600");
            const mediumSeries   = makeSeries("Medium",   "medium",   "C8A300");
            const highSeries     = makeSeries("High",     "high",     "EA5E00");
            const criticalSeries = makeSeries("Critical", "critical", "CD0900");

            // --------- Background per category (BEHIND stacks) ---------
            const bgSeries = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: "Background",
                    xAxis,
                    yAxis,
                    categoryXField: "category",
                    valueYField: "bg",
                    clustered: false,               // ✅ same alignment as stacks
                    stacked: false
                })
            );
            bgSeries.data.setAll(
                normalized
                    .filter(d => d.total > 0)             // ✅ drop zero-total categories
                    .map(d => ({ category: d.category, bg: targetMax }))
                );
            bgSeries.columns.template.setAll({
                fill: am5.color(0xF3F4F6),
                fillOpacity: 1,
                strokeOpacity: 0,
                width: am5.percent(70),
                interactive: false
            });
            bgSeries.set("zIndex", -1000);
            bgSeries.toBack();

            // --------- Dynamic total labels (per bar) ---------
            const addDynamicTotalLabels = (series) => {
                series.bullets.push((root, series, dataItem) => {
                    const total = Number(dataItem.dataContext.total || 0);
                    if (total <= 0) return;
                    return am5.Bullet.new(root, {
                        locationY: 1,
                        sprite: am5.Label.new(root, {
                            text: "{total}",
                            populateText: true,
                            centerX: am5.p50,
                            centerY: am5.p50,
                            dy: -16,
                            fontWeight: "700",
                            fontSize: 16,
                            fill: am5.color("#334569")
                        })
                    });
                });
            };
            // Apply to the top series so only one total label appears per stack
            addDynamicTotalLabels(criticalSeries);

            chart.appear(600, 80);

            return () => root.dispose();
        }
    },[vulnerabilitiesTrend])

    return (
        <div className="bg-white-with-shadow p-4 h-100">
            {
                vulnerabilitiesTrendLoading ?
                <SkeletonLoader type={"line"} count={7} currentPage={"digitalfootprint"} />
                :
                <motion.div
                    className="h-100"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="dashboard-box-container mb-5">
                        <p className="heading font-bold">Vulnerability Trends Over Time</p>
                    </div>
                    <div ref={chartRef} style={{ width: "100%", height: "320px" }} />
                    <div className="severity-list d-flex align-items-center gap-4 mt-3">
                        <p className="severity critical"><span></span>Critical</p>
                        <p className="severity high"><span></span>High</p>
                        <p className="severity medium"><span></span>Medium</p>
                        <p className="severity low"><span></span>Low</p>
                    </div>
                </motion.div>
            }
        </div>
    )
}

export default VulnerabilityTrend;